var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jwtDecode } from 'jwt-decode';
import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { createApiClient } from '../api/createApiClient';
class OidcUser {
    constructor(user) {
        this.user = user;
    }
    get attributes() {
        return this.user.profile;
    }
    get username() {
        // Use sub (UUID) to match backend user ID - email is available via attributes.email
        return this.user.profile.sub;
    }
    get displayName() {
        return this.user.profile.name || this.user.profile.email || this.user.profile.sub;
    }
    get underlyingUserObject() {
        return this.user;
    }
}
class OidcSession {
    constructor(idToken) {
        this.idToken = idToken;
    }
    getIdToken() {
        return this.idToken;
    }
}
class OidcIdToken {
    constructor(jwt) {
        this.jwt = jwt;
        this.payload = jwtDecode(jwt);
    }
    getJwtToken() {
        return this.jwt;
    }
    getExpiration() {
        return this.payload['exp'];
    }
    getIssuedAt() {
        return this.payload['iat'];
    }
}
const TOKEN_EXPIRATION_LEEWAY_SECONDS = 30;
function isJwtUsable(jwt) {
    if (!jwt)
        return false;
    try {
        const payload = jwtDecode(jwt);
        if (!payload.exp)
            return false;
        return payload.exp * 1000 > Date.now() + TOKEN_EXPIRATION_LEEWAY_SECONDS * 1000;
    }
    catch (err) {
        console.warn('Unable to decode OIDC token', err);
        return false;
    }
}
export class CookieStore {
    constructor(domain = undefined, prefix = 'oidc_client') {
        this.domain = domain;
        this.prefix = prefix;
        this._keys = [];
        // Max cookie size ~4KB, but leave room for name, domain, path, etc.
        // Using 3000 bytes per chunk to be safe
        this.CHUNK_SIZE = 3000;
        this.CHUNK_SUFFIX = '_chunk_';
        // First pass: collect all cookies and identify chunks
        const cookies = {};
        const chunkPattern = new RegExp(`^${this.prefix}_(.+)${this.CHUNK_SUFFIX}(\\d+)$`);
        for (const kvp of document.cookie.split(';')) {
            const eqIndex = kvp.indexOf('=');
            if (eqIndex === -1)
                continue;
            const cookieName = kvp.substring(0, eqIndex).trim();
            const cookieValue = kvp.substring(eqIndex + 1).trim();
            if (cookieName.startsWith(this.prefix)) {
                cookies[cookieName] = cookieValue;
            }
        }
        // Second pass: reassemble chunked values
        const processedKeys = new Set();
        for (const cookieName of Object.keys(cookies)) {
            // Check if this is a chunk
            const chunkMatch = cookieName.match(chunkPattern);
            if (chunkMatch) {
                const baseKey = decodeURIComponent(chunkMatch[1]);
                if (processedKeys.has(baseKey))
                    continue;
                // Collect all chunks for this key
                const chunks = [];
                for (const [name, value] of Object.entries(cookies)) {
                    const match = name.match(chunkPattern);
                    if (match && decodeURIComponent(match[1]) === baseKey) {
                        chunks.push({ index: parseInt(match[2], 10), value });
                    }
                }
                // Sort by index and reassemble
                chunks.sort((a, b) => a.index - b.index);
                const fullValue = chunks.map(c => c.value).join('');
                if (!(baseKey in this)) {
                    this[baseKey] = fullValue;
                    this._keys.push(baseKey);
                }
                processedKeys.add(baseKey);
            }
            else {
                // Regular (non-chunked) cookie
                const subKey = decodeURIComponent(cookieName.substring(this.prefix.length + 1));
                if (!(subKey in this) && !processedKeys.has(subKey)) {
                    this[subKey] = cookies[cookieName];
                    this._keys.push(subKey);
                    processedKeys.add(subKey);
                }
            }
        }
    }
    get length() {
        return this._keys.length;
    }
    clear() {
        for (const k of this._keys) {
            delete this[k];
            this.clearCookie(k);
        }
        this._keys.splice(0, this._keys.length);
    }
    getItem(key) {
        if (this._keys.includes(key)) {
            return this[key];
        }
        else {
            return null;
        }
    }
    key(index) {
        if (index >= 0 && index < this._keys.length) {
            return this[this._keys[index]];
        }
        else {
            return null;
        }
    }
    removeItem(key) {
        const ix = this._keys.indexOf(key);
        if (ix >= 0) {
            delete this[key];
            this._keys.splice(ix, 1);
            this.clearCookie(key);
        }
    }
    clearCookie(key) {
        const encodedKey = encodeURIComponent(key);
        const expireAttr = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
        // Only use secure flag on HTTPS to support HTTP deployments
        const secureAttr = window.location.protocol === 'https:' ? 'secure;' : '';
        const baseAttrs = this.domain
            ? `domain=${this.domain}; path=/; ${expireAttr}; ${secureAttr} samesite=lax`
            : `path=/; ${expireAttr}; ${secureAttr} samesite=lax`;
        // Clear the main cookie (in case it was stored without chunking)
        document.cookie = `${this.prefix}_${encodedKey}=; ${baseAttrs}`;
        // And the name a session was written under before names were URI-encoded. The user key
        // always contains ':' (oidc.user:<authority>:<client_id>), so a cookie from before the
        // upgrade sits under the raw name, decodes to the same key, and would otherwise win over
        // the new value on every load until it expired.
        if (encodedKey !== key) {
            document.cookie = `${this.prefix}_${key}=; ${baseAttrs}`;
        }
        // Clear any chunks - scan existing cookies
        const chunkPrefix = `${this.prefix}_${encodedKey}${this.CHUNK_SUFFIX}`;
        for (const kvp of document.cookie.split(';')) {
            const cookieName = kvp.split('=')[0].trim();
            if (cookieName.startsWith(chunkPrefix)) {
                document.cookie = `${cookieName}=; ${baseAttrs}`;
            }
        }
    }
    setItem(key, value) {
        const ix = this._keys.indexOf(key);
        let setCookie = false;
        if (ix >= 0) {
            setCookie = true;
            this[key] = value;
        }
        else {
            // Ignore items that conflict with reserved words (method names)
            if (!(key in this)) {
                setCookie = true;
                this[key] = value;
                this._keys.push(key);
            }
        }
        if (setCookie) {
            // Clear any existing cookies/chunks for this key first
            this.clearCookie(key);
            // expires in 1 week. the OIDC token expiration would take precedence if shorter
            const expires = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toUTCString();
            const encodedKey = encodeURIComponent(key);
            // Only use secure flag on HTTPS to support HTTP deployments
            const secureAttr = window.location.protocol === 'https:' ? 'secure;' : '';
            const baseAttrs = this.domain
                ? `domain=${this.domain}; path=/; expires=${expires}; ${secureAttr} samesite=lax`
                : `path=/; expires=${expires}; ${secureAttr} samesite=lax`;
            if (value.length <= this.CHUNK_SIZE) {
                // Value fits in a single cookie
                document.cookie = `${this.prefix}_${encodedKey}=${value}; ${baseAttrs}`;
            }
            else {
                // Value needs to be chunked
                const numChunks = Math.ceil(value.length / this.CHUNK_SIZE);
                for (let i = 0; i < numChunks; i++) {
                    const chunk = value.substring(i * this.CHUNK_SIZE, (i + 1) * this.CHUNK_SIZE);
                    const chunkCookieName = `${this.prefix}_${encodedKey}${this.CHUNK_SUFFIX}${i}`;
                    document.cookie = `${chunkCookieName}=${chunk}; ${baseAttrs}`;
                }
            }
        }
    }
}
export class KeycloakAuthenticationManager {
    constructor(config) {
        this.config = config;
        this.supportsPasswordSignIn = true;
        this.supportsPasswordChange = true;
        this.accountLinks = {
            parent: this,
            get signUpLink() {
                return `${this.parent.config.authority}/protocol/openid-connect/registrations?client_id=${this.parent.config.client_id}&response_type=code&scope=openid%20email&redirect_uri=${window.location.href}&kc_locale=en`;
            },
            get forgotPasswordLink() {
                return `${this.parent.config.authority}/login-actions/reset-credentials?client_id=${this.parent.config.client_id}`;
            },
        };
        this.userManager = new UserManager({
            authority: config.authority,
            client_id: config.client_id,
            scope: 'openid profile email offline_access',
            redirect_uri: config.redirect_uri,
            automaticSilentRenew: true,
            userStore: new WebStorageStateStore({ store: new CookieStore(config.cookieDomain) }),
        });
    }
    currentAuthenticatedUser() {
        return __awaiter(this, void 0, void 0, function* () {
            const user = yield this.currentValidUser();
            if (user) {
                return new OidcUser(user);
            }
        });
    }
    currentSession() {
        return __awaiter(this, void 0, void 0, function* () {
            const user = yield this.currentValidUser();
            if (user === null || user === void 0 ? void 0 : user.id_token) {
                return new OidcSession(new OidcIdToken(user.id_token));
            }
        });
    }
    currentValidUser() {
        return __awaiter(this, void 0, void 0, function* () {
            const user = yield this.userManager.getUser();
            if ((user === null || user === void 0 ? void 0 : user.id_token) && isJwtUsable(user.id_token)) {
                return user;
            }
            if (user) {
                // The id token is expired or inside the leeway. Try the refresh token before giving the
                // session up, so one missed automatic renewal (a throttled tab, a reload with seconds
                // left) is not a sign-out.
                try {
                    const renewedUser = yield this.userManager.signinSilent();
                    if ((renewedUser === null || renewedUser === void 0 ? void 0 : renewedUser.id_token) && isJwtUsable(renewedUser.id_token)) {
                        return renewedUser;
                    }
                }
                catch (err) {
                    console.warn('Silent Keycloak token renewal failed', err);
                }
                yield this.userManager.removeUser();
            }
        });
    }
    signIn(email, password) {
        return __awaiter(this, void 0, void 0, function* () {
            const user = yield this.userManager.signinResourceOwnerCredentials({
                username: email,
                password: password,
            });
            return new OidcUser(user);
        });
    }
    signOut() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.userManager.removeUser();
            window.location.href = '/login';
        });
    }
    changePassword(user, oldPassword, newPassword) {
        return __awaiter(this, void 0, void 0, function* () {
            const torqCloudApiClient = createApiClient(this, this.config.torqCloudBaseUrl, { 'Content-type': 'application/json' });
            yield torqCloudApiClient.post(`/api/v1/users/change_password/`, { oldPassword, newPassword });
        });
    }
}
//# sourceMappingURL=Keycloak.js.map